import client from '@/services/client'
import type { IGetBaseModelResult } from '@/types/flow/FlowPayloadTypes'
import type { IPathwayFlowsResponse } from '@/types/PathawayLandingPageTypes'
import type {
  IPathwaySubmitBody,
  IPathwayCreateResult,
  IPathwayFlowResultTree,
  IPathwaySelectOptionResult,
  IPathwaySubmitResult,
  ModelAssignment,
  ISelectControlValuePayload,
  FindOption,
  ISelectActionsSubmit,
  SelectOptionSelect,
} from '@/types/PathwayTypes'

import type { AxiosResponse } from 'axios'

const create = async (
  flowUuid: string
): Promise<AxiosResponse<IPathwayCreateResult>> => {
  return await client.post<IPathwayCreateResult>(`pathways/drafts`, {
    flowUuid: flowUuid,
  })
}

const findFlowDraft = async (
  flowUuid: string
): Promise<AxiosResponse<IPathwaySelectOptionResult>> => {
  return await client.get<IPathwaySelectOptionResult>(
    `pathways/drafts/${flowUuid}`
  )
}

const getAll = async (): Promise<AxiosResponse<IPathwayFlowsResponse[]>> => {
  return await client.get<IPathwayFlowsResponse[]>(
    `pathways/flows/configuration`
  )
}

const findFlow = async (
  flowUuid: string
): Promise<AxiosResponse<IPathwayFlowResultTree>> => {
  return await client.get<IPathwayFlowResultTree>(
    `pathways/flows/${flowUuid}?fetchMode=tree`
  )
}

const getOptionModel = async (
  optionUuid: string
): Promise<AxiosResponse<IGetBaseModelResult[]>> => {
  return await client.get<IGetBaseModelResult[]>(
    `pathways/flows/options/${optionUuid}/models`
  )
}

const downloadModel = async (
  model3DUuid: string
): Promise<AxiosResponse<IGetBaseModelResult>> => {
  return await client.get<IGetBaseModelResult>(
    `pathways/flows/models/${model3DUuid}`
  )
}

const findOption = async (
  optionUuid: string
): Promise<AxiosResponse<FindOption>> => {
  return await client.get<FindOption>(`pathways/flows/options/${optionUuid}`)
}

const selectOptionValue = async (
  payload: SelectOptionSelect
): Promise<AxiosResponse<IPathwaySelectOptionResult>> =>
  await client.patch<IPathwaySelectOptionResult>(
    `pathways/drafts/${payload.pathwayUuid}/actions`,
    {
      optionUuid: payload.optionUuid,
      stepUuid: payload.stepUuid,
      action: payload.action,
      value: payload.value,
    }
  )

const selectOptionSubmitReject = async (
  pathwayUuid: string,
  action: ISelectActionsSubmit
): Promise<AxiosResponse<IPathwaySelectOptionResult>> =>
  await client.patch<IPathwaySelectOptionResult>(
    `pathways/drafts/${pathwayUuid}/actions`,
    {
      action: action,
    }
  )

const selectControlValue = async (
  pathwayUuid: string,
  payload: ISelectControlValuePayload
): Promise<AxiosResponse<IPathwaySelectOptionResult>> =>
  client.patch(`pathways/drafts/${pathwayUuid}/actions`, {
    ...payload,
    action: 'updateControlValue',
  })

const acceptPackage = async (
  pathwayUuid: string,
  packageUuid: string
): Promise<AxiosResponse<IPathwaySelectOptionResult>> =>
  client.patch(`pathways/drafts/${pathwayUuid}/actions`, {
    action: 'acceptPackage',
    packageUuid,
  })

const submit = async (
  pathwayUuid: string,
  payload: IPathwaySubmitBody
): Promise<AxiosResponse<IPathwaySubmitResult>> =>
  await client.post<IPathwaySubmitResult>(
    `pathways/drafts/${pathwayUuid}/submit`,
    { ...payload }
  )

const getModelsAssignments = async (
  flowUuid: string
): Promise<AxiosResponse<ModelAssignment[]>> =>
  await client.get<ModelAssignment[]>(
    `pathways/flows/${flowUuid}/model-assignments`
  )

export const PathwayRepository = {
  create,
  getAll,
  findFlowDraft,
  findFlow,
  findOption,
  selectOptionValue,
  selectOptionSubmitReject,
  downloadModel,
  getOptionModel,
  submit,
  getModelsAssignments,
  selectControlValue,
  acceptPackage,
}
